import { call, cancel, fork, put } from 'redux-saga/effects';
import Debug from 'debug';
import { whenCapable } from '../Feature/capable';
import { ManagementCapabilities } from '@signageos/common-types/dist/Device/Capabilities/ManagementCapabilities';
import ManagementCapability from '../NativeDevice/Management/ManagementCapability';
import { withDependencies } from '../DI/dependencyInjection';
import {
	PluginSet,
	PluginSetFailed,
	PluginSetSucceeded,
	PluginGet,
	PluginGetFailed,
	PluginGetSucceeded,
	UnAssignDevicePlugin,
	AssignDevicePlugin,
	UpdateAssignDevicePlugin,
	ReloadPlugin,
	UpdateAssignedPlugins,
	PluginReloadSucceeded,
} from '@signageos/actions/dist/Device/Plugin/pluginActions';
import Responsibility from '../Feature/Responsibility';
import { whenResponsible } from '../Feature/responsible';
import OfflineCache from '../OfflineCache/OfflineCache';
import { getConfiguration } from '../CustomScripts/utils';
import { AppletBinaryDownloader } from '../Front/Applet/AppletBinaryDownloader';
import { IFile } from '../NativeDevice/fileSystem';
import { bindAndTakeEveryOnlyWhenPlatform } from '../Socket/socketActionCreator';
import { PluginDownloader } from './pluginDownloader';
import { BrowserRuntime } from '../CustomScripts/Runtimes/browserRuntime';
import Property from '../Property/Property';
import { buffers, channel } from 'redux-saga';
import IManagementDriver from '../NativeDevice/Management/IManagementDriver';
import { IPropertyStorage } from '../Property/propertyStorage';
import { Plugin } from '@signageos/common-types/dist/Plugin/Plugin';

// Constants for plugin operations
const PLUGIN_WAIT_TIMEOUT = 2000;
const PLUGIN_CHECK_INTERVAL = 200;
const PLUGIN_MAX_RETRIES = 20;
/**
 * Utility function to check if plugin archive has changed
 */
function hasPluginArchiveChanged(stored: AssignedPlugins[string], plugin: Plugin): boolean {
	return stored.archiveUri !== plugin.archiveUri || stored.md5Checksum !== plugin.md5Checksum;
}

/**
 * Utility function to check if applet version has changed
 */
function hasAppletVersionChanged(stored: AssignedPlugins[string], assignment: any): boolean {
	return assignment.frontAppletVersion && stored.pluginVersion !== assignment.pluginVersion;
}

/**
 * Utility function to remove old iframe for plugin
 */
function removePluginIframe(md5Checksum: string): void {
	const iframe = document.getElementById(md5Checksum) as HTMLIFrameElement;
	if (iframe?.parentNode) {
		iframe.parentNode.removeChild(iframe);
	}
}

/**
 * Utility function to update plugin archive
 */
function* updatePluginArchive(pluginDownloader: PluginDownloader, pluginAssignment: Plugin, updatedPlugin: AssignedPlugins[string]): any {
	const pluginFile: IFile | undefined = yield pluginDownloader.getOrDownloadPlugin(
		pluginAssignment.archiveUri,
		pluginAssignment.mainFile,
		pluginAssignment.md5Checksum,
	);

	return {
		...updatedPlugin,
		archiveUri: pluginAssignment.archiveUri,
		mainFile: pluginAssignment.mainFile,
		md5Checksum: pluginAssignment.md5Checksum,
		pluginFile,
		active: false,
		iframeId: undefined,
	};
}

/**
 * Utility function to update applet version
 */
function* updateAppletVersion(appletBinaryDownloader: any, assignment: any, updatedPlugin: AssignedPlugins[string]): any {
	const appletJsFile: IFile | undefined = yield appletBinaryDownloader.getOrDownloadFrontAppletJsFile(assignment.frontAppletVersion);

	return {
		...updatedPlugin,
		appletJsFile,
		pluginVersion: assignment.pluginVersion,
	};
}

export interface AssignedPlugins {
	[assignmentUid: string]: {
		archiveUri: string;
		pluginVersion: string;
		mainFile: string;
		pluginUid: string;
		assignmentUid?: string;
		md5Checksum: string;
		runtime: string;
		appletJsFile?: IFile;
		pluginFile?: IFile;
		configuration?: Record<string, any>;
		encryptedConfigurationFields?: string[];
		assignedAt?: string;
		active: boolean;
		iframeId?: string;
		lastExecuted?: string;
	};
}

const debug = Debug('@signageos/front-display:Plugins:pluginsSaga');

function* getAllAssignedPlugins(propertyStorage: any): any {
	return yield propertyStorage.getValueOrDefault(Property.DEVICE_PLUGINS, {});
}

function* getAssignedPlugin(propertyStorage: IPropertyStorage, assignmentUid: string) {
	const assignedPlugins: AssignedPlugins = yield* getAllAssignedPlugins(propertyStorage);
	return assignedPlugins[assignmentUid] || null;
}

function* savePlugin(propertyStorage: IPropertyStorage, assignmentUid: string, pluginData: AssignedPlugins[string]) {
	const currentPlugins = yield* getAllAssignedPlugins(propertyStorage);
	const updatedPlugins = {
		...currentPlugins,
		[assignmentUid]: {
			...pluginData,
			assignedAt: new Date().toISOString(),
		},
	};
	yield propertyStorage.setValue(Property.DEVICE_PLUGINS, updatedPlugins);
}

function* removePlugin(propertyStorage: IPropertyStorage, assignmentUid: string): any {
	const currentPlugins = yield* getAllAssignedPlugins(propertyStorage);
	const { [assignmentUid]: _removed, ...remainingPlugins } = currentPlugins;
	yield propertyStorage.setValue(Property.DEVICE_PLUGINS, remainingPlugins);
}

/**
 * Utility function to wait for sosPlugin module to be available in iframe
 */
function* waitForSosPlugin(iframeId: string, pluginUid: string, methodName: string = 'set'): any {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	if (!iframe?.contentWindow) {
		throw new Error(`Iframe with id ${iframeId} not found or has no contentWindow`);
	}

	const iframeWindow = iframe.contentWindow as any;

	const waitForModule = (retries = PLUGIN_MAX_RETRIES): Promise<any> => {
		return new Promise((resolve, reject) => {
			const checkModule = () => {
				if (iframeWindow?.sosPlugin?.[methodName]) {
					resolve(iframeWindow.sosPlugin);
				} else if (retries > 0) {
					setTimeout(checkModule, PLUGIN_CHECK_INTERVAL);
					retries--;
				} else {
					reject(new Error(`sosPlugin.${methodName} not available for plugin ${pluginUid}`));
				}
			};
			checkModule();
		});
	};

	return yield call(waitForModule);
}

/**
 * Utility function to get iframe and its window for plugin
 */
function getPluginIframeWindow(iframeId: string): { iframe: HTMLIFrameElement | null; iframeWindow: any } {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	const iframeWindow = iframe?.contentWindow as any;
	return { iframe, iframeWindow };
}

/**
 * Utility function to create and run plugin in iframe
 */
function* createAndRunPlugin(
	browserRuntime: BrowserRuntime,
	managementDriver: IManagementDriver,
	plugin: AssignedPlugins[string],
	configuration: Record<string, any>,
): any {
	const messageChannel = channel<MessageEvent>(buffers.dropping(100));
	const messagesHandler = (event: MessageEvent) => {
		messageChannel.put(event);
	};

	yield call(
		browserRuntime.runPluginInBrowser,
		managementDriver,
		plugin,
		configuration,
		plugin.pluginFile,
		plugin.appletJsFile,
		messagesHandler,
	);

	const iframeId = plugin.md5Checksum;
	const binMessages = yield fork(browserRuntime.bindCustomScriptsMessages, messageChannel, window, iframeId);

	// Wait for iframe to load
	yield new Promise((resolve) => setTimeout(resolve, PLUGIN_WAIT_TIMEOUT));

	return { iframeId, binMessages };
}

/**
 * Utility function to cleanup plugin iframe
 */
function* cleanupPluginIframe(iframeId: string, binMessages: any): any {
	const { iframe } = getPluginIframeWindow(iframeId);
	if (iframe?.parentNode) {
		iframe.parentNode.removeChild(iframe);
		yield cancel(binMessages);
	}
}

export const pluginsSaga = whenResponsible(Responsibility.PLUGINS, whenCapable(ManagementCapabilities.PLUGINS, pluginsSagaDefault));

function* pluginsSagaDefault(offlineCache: OfflineCache) {
	yield fork(
		whenCapable(
			ManagementCapability.PLUGINS,
			withDependencies(
				['managementDriver', 'staticBaseUrl', 'propertyStorage', 'featureFlags'],
				function* ({ managementDriver, staticBaseUrl, propertyStorage, featureFlags }) {
					if (!(featureFlags?.plugins ?? true)) {
						return;
					}

					const getStaticBaseUrl = () => {
						return staticBaseUrl;
					};

					const browserRuntime = new BrowserRuntime();
					const pluginDownloader = new PluginDownloader(offlineCache, getStaticBaseUrl);

					const appletBinaryDownloader = new AppletBinaryDownloader(false, offlineCache, getStaticBaseUrl);

					yield bindAndTakeEveryOnlyWhenPlatform(UpdateAssignedPlugins, function* (action: UpdateAssignedPlugins) {
						const assignedPlugins = yield* getAllAssignedPlugins(propertyStorage);
						const incomingAssignments = action.assignments || [];

						const newAssignments = incomingAssignments.filter((assignment) => !assignedPlugins[assignment.assignmentUid]);

						for (const assignment of newAssignments) {
							try {
								const pluginFile = yield pluginDownloader.getOrDownloadPlugin(
									assignment.plugin.archiveUri,
									assignment.plugin.mainFile,
									assignment.plugin.md5Checksum,
								);
								const pluginData = {
									archiveUri: assignment.plugin.archiveUri,
									mainFile: assignment.plugin.mainFile,
									md5Checksum: assignment.plugin.md5Checksum,
									runtime: assignment.plugin.runtime,
									pluginVersion: assignment.pluginVersion,
									pluginUid: assignment.pluginUid,
									assignmentUid: assignment.assignmentUid,
									configuration: assignment.configuration,
									encryptedConfigurationFields: assignment.encryptedConfigurationFields,
									pluginFile,
									active: false,
								};
								yield* savePlugin(propertyStorage, assignment.assignmentUid, pluginData);
							} catch (error) {
								console.error(`Error when processing ${assignment.assignmentUid}:`, error);
							}
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(PluginSet, function* (action: PluginSet) {
						let iframeId: string | null = null;
						let binMessages: any = null;

						try {
							const plugin = yield* getAssignedPlugin(propertyStorage, action.assignmentUid);
							if (!plugin) {
								yield put<PluginSetFailed>({
									type: PluginSetFailed,
									uid: action.uid,
									error: `Plugin with assignment UID ${action.assignmentUid} not found.`,
								});
								return;
							}

							const configuration: Record<string, any> = yield call(
								getConfiguration,
								plugin.configuration ?? {},
								plugin.encryptedConfigurationFields ?? [],
								managementDriver,
							);

							const result = yield* createAndRunPlugin(browserRuntime, managementDriver, plugin, configuration);
							if (!result) {
								throw new Error('Failed to create and run plugin');
							}

							iframeId = result.iframeId;
							binMessages = result.binMessages;

							if (!iframeId) {
								throw new Error('IframeId is null');
							}
							const sosPlugin = yield* waitForSosPlugin(iframeId, plugin.pluginUid);

							yield call(sosPlugin.set, action.data);

							yield put<PluginSetSucceeded>({
								type: PluginSetSucceeded,
								uid: action.uid,
							});
						} catch (error) {
							yield put<PluginSetFailed>({
								type: PluginSetFailed,
								uid: action.uid,
								error: error.message,
							});
						} finally {
							if (iframeId && binMessages) {
								yield* cleanupPluginIframe(iframeId, binMessages);
							}
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(PluginGet, function* (action: PluginGet) {
						let iframeId: string | null = null;
						let binMessages: any = null;

						try {
							const plugin = yield* getAssignedPlugin(propertyStorage, action.assignmentUid);
							if (!plugin) {
								yield put<PluginGetFailed>({
									type: PluginGetFailed,
									uid: action.uid,
									error: `Plugin with assignment UID ${action.assignmentUid} not found.`,
								});
								return;
							}

							const configuration: Record<string, any> = yield call(
								getConfiguration,
								plugin.configuration ?? {},
								plugin.encryptedConfigurationFields ?? [],
								managementDriver,
							);

							const result = yield* createAndRunPlugin(browserRuntime, managementDriver, plugin, configuration);
							if (!result) {
								throw new Error('Failed to create and run plugin');
							}

							iframeId = result.iframeId;
							binMessages = result.binMessages;

							if (!iframeId) {
								throw new Error('IframeId is null');
							}
							const sosPlugin = yield* waitForSosPlugin(iframeId, plugin.pluginUid, 'get');

							const getData = yield call(sosPlugin.get);

							yield put<PluginGetSucceeded>({
								type: PluginGetSucceeded,
								uid: action.uid,
								data: getData,
							});
						} catch (error) {
							yield put<PluginGetFailed>({
								type: PluginGetFailed,
								uid: action.uid,
								error: error.message,
							});
						} finally {
							if (iframeId && binMessages) {
								yield* cleanupPluginIframe(iframeId, binMessages);
							}
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(AssignDevicePlugin, function* (action: AssignDevicePlugin) {
						let pluginFile: IFile | undefined;
						try {
							pluginFile = yield pluginDownloader.getOrDownloadPlugin(
								action.plugin.archiveUri,
								action.plugin.mainFile,
								action.plugin.md5Checksum,
							);
						} catch (error) {
							console.error(`Failed to download plugin: ${error}`);
						}

						let appletJsFile: IFile | undefined = undefined;

						if (action.frontAppletVersion) {
							appletJsFile = yield appletBinaryDownloader.getOrDownloadFrontAppletJsFile(action.frontAppletVersion);
						}

						debug('appletJsFile', appletJsFile);

						yield call(savePlugin, propertyStorage, action.assignmentUid, {
							archiveUri: action.plugin.archiveUri,
							mainFile: action.plugin.mainFile,
							pluginFile,
							appletJsFile,
							pluginUid: action.pluginUid,
							pluginVersion: action.pluginVersion,
							md5Checksum: action.plugin.md5Checksum,
							runtime: action.plugin.runtime,
							configuration: action.configuration,
							encryptedConfigurationFields: action.encryptedConfigurationFields,
							active: false,
							iframeId: action.plugin.md5Checksum,
							lastExecuted: undefined,
						});
					});

					yield bindAndTakeEveryOnlyWhenPlatform(UnAssignDevicePlugin, function* (action: UnAssignDevicePlugin) {
						try {
							yield* removePlugin(propertyStorage, action.assignmentUid);
						} catch (error) {
							console.error('UnAssignDevicePlugin error:', error);
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(UpdateAssignDevicePlugin, function* (action: UpdateAssignDevicePlugin) {
						try {
							const existingPlugin = yield* getAssignedPlugin(propertyStorage, action.assignmentUid);
							if (!existingPlugin) {
								console.error(`Plugin with assignment UID ${action.assignmentUid} not found for update.`);
								return;
							}

							const updatedPlugin: AssignedPlugins[string] = {
								...existingPlugin,
								...action,
								configuration: action.configuration,
								encryptedConfigurationFields: action.encryptedConfigurationFields,
							};

							yield* savePlugin(propertyStorage, action.assignmentUid, updatedPlugin);
						} catch (error) {
							console.error('UpdateAssignDevicePlugin error:', error);
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(ReloadPlugin, function* (action: ReloadPlugin) {
						try {
							const assignedPlugins: AssignedPlugins = yield* getAllAssignedPlugins(propertyStorage);
							const storedPlugin = assignedPlugins[action.assignmentUid];

							if (!storedPlugin) {
								console.warn(`Plugin with assignment UID ${action.assignmentUid} not found for reload`);
								return;
							}

							let needsUpdate = false;
							let updatedPlugin = { ...storedPlugin, ...action.plugin };

							// Check and update plugin archive if changed
							if (hasPluginArchiveChanged(storedPlugin, action.plugin)) {
								needsUpdate = true;
								removePluginIframe(storedPlugin.md5Checksum);
								updatedPlugin = yield* updatePluginArchive(pluginDownloader, action.plugin, updatedPlugin);
							}

							// Check and update applet version if changed
							if (hasAppletVersionChanged(storedPlugin, action.assignmentUid)) {
								needsUpdate = true;
								updatedPlugin = yield* updateAppletVersion(appletBinaryDownloader, action.assignmentUid, updatedPlugin);
							}

							// Save updated plugin if any changes were made
							if (needsUpdate) {
								yield* savePlugin(propertyStorage, action.assignmentUid, updatedPlugin);
							}
							yield put<PluginReloadSucceeded>({
								type: PluginReloadSucceeded,
								uid: action.uid,
							});
						} catch (error) {
							console.error('Error in ReloadPlugin handler:', error);
						}
					});
				},
			),
		),
	);
}
